import { DeleteOutline } from '@mui/icons-material';
import AnalyticsOutlined from '@mui/icons-material/AnalyticsOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import ShareOutlined from '@mui/icons-material/ShareOutlined';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import {
  Skeleton,
  Stack,
  Switch,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgentRoles } from '../../../contexes/AgentRolesProvider';
import AuthContext from '../../../contexes/AuthContext';
import DashboardContext from '../../../contexes/DashboardContext';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import {
  getAgentPstnNumber,
  getAgentSipAddress,
  hasCustomPhoneNumber,
} from '../../../helpers/agentHelper';
import { useAnalytics } from '../../../hooks/useAnalytics';
import useAgentStatusToggle from '../../../hooks/useAgentStatusToggle';
import useNavigateToAgentDashboard from '../../../hooks/useNavigateToAgentDashboard';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  AgentStatus as AgentStatusInterface,
  AgentWithAttachedKnowledge,
} from '../../../interfaces/Agent';
import { ResponseObject } from '../../../interfaces/App';
import { ResourceAccess } from '../../../interfaces/ResourceAccess';
import { deleteAgent, updateAgent } from '../../../services/agentService';
import TransferAgentDialog from '../../admin/TransferAgentDialog';
import Button from '../../shared/Button/Button';
import Modal from '../../shared/Modal/Modal';
import ResourceVisibilityBadge from '../../shared/ResourceVisibilityBadge/ResourceVisibilityBadge';
import ShareResourceDialog from '../../shared/ShareResourceDialog/ShareResourceDialog';
import AgentAvatar from '../../shared/Table/AgentAvatar';
import AgentStatus from '../../shared/Table/AgentStatus';
import TextTruncated from '../../shared/TextTruncated/TextTruncated';
import {
  canManageAgentAccess,
  canEditAgentResource,
  toAccessPayload,
} from '../../../utils/resourceAccess';

interface TableItemProps {
  agent: AgentWithAttachedKnowledge;
  agents: AgentWithAttachedKnowledge[];
}

const TableItem = ({ agent, agents }: TableItemProps) => {
  const { t } = useTranslation();
  const { navigateToAgentDashboard } = useNavigateToAgentDashboard();
  const navigate = useNavigate();
  const displayMessage = useSnackBar();
  const { setAgentsCount } = useContext(DashboardContext);
  const { currentUser } = useContext(AuthContext);
  const { track } = useAnalytics();
  const queryClient = useQueryClient();

  const { getRoleById, loading } = useAgentRoles();

  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const canShare = canManageAgentAccess(currentUser, agent);
  const canEdit = canEditAgentResource(currentUser, agent);

  const [agentPhoneNumber, setAgentPhoneNumber] = useState<string | undefined>(
    getAgentPstnNumber(agent) || undefined
  );
  const actionIconButtonStyle = {
    minWidth: '44px',
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    background: '#F1F5F9',
    borderColor: 'rgba(15, 23, 42, 0.06)',
    color: '#1841ff',
    padding: '0.5rem',
  };

  // Use the custom hook for status toggling
  const {
    agentStatus,
    isUpdating,
    onStatusClick,
    changeStatusModalOpen,
    setChangeStatusModalOpen,
  } = useAgentStatusToggle({
    agentId: agent.id,
    agentName: agent.name,
    initialStatus: agent.status || '',
    updateService: updateAgent,
    requireSubscription: true,
    requiresPaidPlanForOnline: true,
    analyticsSource: 'agent_table_page',
    onStatusChangeSuccess: updatedAgentData => {
      // Update the UI of the phone number assigned to the agent
      if (
        updatedAgentData.status === AgentStatusInterface.ONLINE &&
        updatedAgentData.system_phone_number
      ) {
        setAgentPhoneNumber(getAgentPstnNumber(updatedAgentData) || undefined);
      }
    },
  });

  const navigateToEditAgent = (agentId: string) => {
    navigate(`/agents/edit/${agentId}`);
  };

  const updateAgents = (agentId: string) => {
    return agents.filter(agent => agent.id !== agentId);
  };

  const handleDeleteAgent = async (agentId: string) => {
    track(AnalyticsEvents.AGENT_DELETION_ATTEMPTED, { agentId });

    try {
      const result: ResponseObject = await deleteAgent(agentId);

      if (result.status === 'success') {
        const newAgents = updateAgents(agentId);
        setAgentsCount(newAgents?.length);

        // Invalidate the agents query to force a refetch
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['agents'] }),
          queryClient.refetchQueries({
            queryKey: ['knowledges-with-attachment'],
          }),
        ]);

        displayMessage(result.message, 'success');

        track(AnalyticsEvents.AGENT_DELETION_SUCCESS, { agentId });
      } else {
        track(AnalyticsEvents.AGENT_DELETION_FAILURE, {
          agentId,
          reason: result.message,
        });
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        displayMessage(
          t('Oops! Something went wrong, please reload the page.'),
          'error'
        );
        track(AnalyticsEvents.AGENT_DELETION_FAILURE, {
          agentId,
          reason: err.message,
        });
      }
    }
  };

  const handleSaveAccess = async (access: ResourceAccess) => {
    try {
      await updateAgent(agent.id!, { access: toAccessPayload(access) });
      await queryClient.invalidateQueries({ queryKey: ['agents'] });
      displayMessage(t('Access updated.'), 'success');
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
      }
      displayMessage(t('Failed to update access.'), 'error');
    }
  };

  return (
    <TableRow
      key={agent.name}
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell component="th" scope="row">
        <AgentAvatar
          agentName={agent.name}
          agentId={agent.id}
          imageFileName={agent.avatar ?? ''}
          voice={agent.voice}
        />
      </TableCell>
      <TableCell align="center">
        {loading ? (
          <Skeleton variant="text" />
        ) : (
          getRoleById(agent.role_id)?.name
        )}
      </TableCell>
      <TableCell align="center">
        <ResourceVisibilityBadge access={agent.access} />
      </TableCell>
      <TableCell align="center">
        <TextTruncated title={agent.attachedKnowledge?.name} width="100px">
          {agent.attachedKnowledge?.name}
        </TextTruncated>
      </TableCell>
      <TableCell align="center">
        <div>
          {agentPhoneNumber ? <div>{agentPhoneNumber}</div> : null}
          {getAgentSipAddress(agent) ? (
            <div>{getAgentSipAddress(agent)}</div>
          ) : null}
          {!agentPhoneNumber && !getAgentSipAddress(agent) ? '—' : null}
        </div>
        {hasCustomPhoneNumber(agent) && (
          <Tooltip
            title={t('Please setup your phone to redirect to {{number}}', {
              number: agent.system_phone_number,
            })}
            placement="top"
            arrow
          >
            <small className="setup-redirect-label">
              {t('SETUP REDIRECT')}
            </small>
          </Tooltip>
        )}
      </TableCell>
      <TableCell align="center">
        <Stack direction="row" spacing={1}>
          <Switch
            name="status"
            onClick={onStatusClick}
            disabled={!canEdit || isUpdating}
            checked={
              agentStatus === AgentStatusInterface.ONLINE ||
              agentStatus === AgentStatusInterface.IN_REVIEW
            }
            sx={{
              width: 42,
              height: 22,
              p: 0,
              '& .MuiSwitch-switchBase': {
                p: '2px',
                transitionDuration: '180ms',
                '&.Mui-checked': {
                  transform: 'translateX(20px)',
                  color: '#FFFFFF',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#2147F6',
                    opacity: 1,
                  },
                },
              },
              '& .MuiSwitch-thumb': {
                width: 18,
                height: 18,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.25)',
              },
              '& .MuiSwitch-track': {
                borderRadius: 11,
                backgroundColor: '#D1D5DB',
                opacity: 1,
              },
            }}
          />
          <AgentStatus status={agentStatus as AgentStatusInterface} />
          <Modal
            modalShouldBeOpen={changeStatusModalOpen}
            setChangeStatusModalOpen={setChangeStatusModalOpen}
            showButton={false}
            item={agent}
            callback={() => navigate('/pricing')}
            mainPromptText={t(
              'In order to change the agent status to online you need to have an active subscription'
            )}
            description={t('Subscribe to a plan and try again')}
            confirmText={t('Choose plan')}
            denyText={t('Cancel')}
          />
        </Stack>
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {currentUser?.permissions?.super_admin && (
            <>
              <Tooltip
                title={t('Transfer to client')}
                placement="right-start"
                arrow
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    padding="0.5rem"
                    style={actionIconButtonStyle}
                    onClick={() => setTransferDialogOpen(true)}
                  >
                    <SwapHorizOutlined />
                  </Button>
                </span>
              </Tooltip>
              {transferDialogOpen && (
                <TransferAgentDialog
                  agentId={agent.id as string}
                  agentName={agent.name}
                  agentType="call"
                  onClose={() => setTransferDialogOpen(false)}
                  onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['agents'] });
                  }}
                />
              )}
            </>
          )}
          {canShare && (
            <>
              <Tooltip title={t('Share')} placement="right-start" arrow>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    padding="0.5rem"
                    style={actionIconButtonStyle}
                    onClick={() => setShareDialogOpen(true)}
                  >
                    <ShareOutlined />
                  </Button>
                </span>
              </Tooltip>
              <ShareResourceDialog
                open={shareDialogOpen}
                onClose={() => setShareDialogOpen(false)}
                resourceName={agent.name}
                resourceLabelKey="call agent"
                ownerUserId={agent.user_id ?? currentUser?.id}
                initialAccess={agent.access}
                onSave={handleSaveAccess}
              />
            </>
          )}
          <Tooltip
            title={t('View analytics dashboard')}
            placement="right-start"
            arrow
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                padding="0.5rem"
                style={actionIconButtonStyle}
                onClick={() => navigateToAgentDashboard(agent?.id)}
              >
                <AnalyticsOutlined />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={t('Edit attributes')} placement="right-start" arrow>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                padding="0.5rem"
                style={actionIconButtonStyle}
                onClick={() => navigateToEditAgent(agent?.id as string)}
                disabled={!canEdit}
              >
                <EditOutlined />
              </Button>
            </span>
          </Tooltip>
          {canEdit && (
            <Modal
              item={agent}
              callback={handleDeleteAgent}
              mainPromptText={t(
                'Do you really want to delete agent {{name}}?',
                {
                  name: agent.name,
                }
              )}
              confirmText={t('Yes, Delete')}
              denyText={t('Cancel')}
              openModalButtonText="" // explicitly remove the default label
              toolTipText={t('Delete')}
              openModalIcon={<DeleteOutline />}
            />
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default TableItem;
